import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { fmtDate, inr, pct } from "../../lib/format";
import { ArrowLeft, ChevronRight } from "lucide-react";
import PortfolioOverview from "../../components/PortfolioOverview";

export default function CustomerPortfolio() {
  const { user, refresh } = useAuth();
  const { pid } = useParams();
  const navigate = useNavigate();
  const [portfolios, setPortfolios] = useState(null);
  const [overview, setOverview] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadList = () => {
    api.get("/customer/portfolios").then((r) => setPortfolios(r.data)).catch(() => setPortfolios([]));
  };
  const loadOne = (id) => {
    api.get(`/customer/portfolios/${id}/overview`)
      .then((r) => setOverview(r.data))
      .catch((err) => {
        // 400 = "needs initial value" (portfolio allotted but not activated by customer yet)
        // 404 = actually not found
        const status = err.response?.status;
        const detail = err.response?.data?.detail || "";
        if (status === 400 && /initial value/i.test(detail)) {
          setOverview({ needs_value: true, portfolio: { portfolio_id: id } });
        } else {
          setOverview({ error: true });
        }
      });
  };

  useEffect(() => {
    if (pid) {
      loadOne(pid);
      if (!portfolios) loadList(); // also load list so we can show "Portfolio X of Y"
    } else {
      setOverview(null);
      loadList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid]);

  // ===== DRILL-DOWN VIEW =====
  if (pid) {
    if (!overview) return <div className="text-zinc-500">Loading...</div>;
    // Distinguish "portfolio not found" from "needs initial value" — the latter is a NORMAL first-login state.
    if (overview.error && !overview.needs_value) return <div className="text-loss">Portfolio not found.</div>;
    const port = overview.portfolio || {};
    const stratName = port.strategy_name || overview.strategy_name || "Portfolio";
    const totalPorts = (portfolios || []).length;
    const portIdx = (portfolios || []).findIndex((p) => p.portfolio_id === pid) + 1;
    return (
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <Link to="/portfolio" className="text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1">
              <ArrowLeft size={12} /> All my portfolios
            </Link>
            <h1 className="font-display font-bold text-3xl text-zinc-950 mt-1" data-testid="customer-portfolio-strategy-name">{stratName}</h1>
            <div className="mt-2 flex items-center gap-3 flex-wrap" data-testid="customer-portfolio-header">
              <Badge className="bg-purple-50 text-purple-700 border-purple-200 font-display font-semibold">{stratName}</Badge>
              {(port.start_date || port.end_date) && (
                <div className="text-xs text-zinc-500 font-mono">
                  {fmtDate(port.start_date)} → {fmtDate(port.end_date)}
                </div>
              )}
              {port.is_complimentary && <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Complimentary</Badge>}
              {totalPorts > 1 && portIdx > 0 && (
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
                  Portfolio {portIdx} of {totalPorts}
                </div>
              )}
            </div>
          </div>
        </div>
        {overview.needs_value ? (
          <DefineValueForm portfolioId={pid} onDone={() => loadOne(pid)} refresh={refresh} />
        ) : (
          <PortfolioOverview data={overview} />
        )}
      </div>
    );
  }

  // ===== LIST VIEW =====
  if (!portfolios) return <div className="text-zinc-500">Loading...</div>;
  if (portfolios.length === 0) {
    return (
      <Card className="border-zinc-200 shadow-none max-w-2xl mx-auto">
        <CardContent className="p-10 text-center">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">No Portfolio</div>
          <h2 className="font-display font-bold text-2xl text-zinc-950 mt-3">No active portfolio</h2>
          <p className="text-sm text-zinc-500 mt-2">Your administrator has not yet allotted a portfolio. Please contact your relationship manager.</p>
        </CardContent>
      </Card>
    );
  }

  const needsValueCount = (portfolios || []).filter((p) => !p.initial_value).length;
  const firstNeedsValue = (portfolios || []).find((p) => !p.initial_value);
  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Portfolio</div>
        <h1 className="font-display font-bold text-3xl text-zinc-950 mt-1">{user?.name?.split(" ")[0]}&apos;s Portfolios</h1>
        <p className="text-sm text-zinc-500 mt-1">You have {portfolios.length} portfolio subscription{portfolios.length > 1 ? "s" : ""}. Click any card to drill into KPIs, holdings, and the comparison chart.</p>
      </div>

      {needsValueCount > 0 && firstNeedsValue && (
        <Card
          data-testid="needs-value-banner"
          className="border-amber-300 bg-amber-50 shadow-none cursor-pointer hover:bg-amber-100 transition-colors"
          onClick={() => navigate(`/portfolio/${firstNeedsValue.portfolio_id}`)}
        >
          <CardContent className="p-5 flex items-center justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700 font-semibold">Action required</div>
              <div className="font-display font-bold text-xl text-amber-900 mt-1">
                Define initial value of your portfolio to activate this portfolio
              </div>
              <div className="text-sm text-amber-800 mt-1">
                {needsValueCount === 1
                  ? "Your newly allotted portfolio needs an initial value before we can start tracking returns and generating recommendations."
                  : `${needsValueCount} portfolios are awaiting an initial value.`}
              </div>
            </div>
            <Button className="bg-amber-900 hover:bg-amber-800 text-white whitespace-nowrap">
              Activate now →
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {portfolios.map((p) => (
          <PortfolioCard key={p.portfolio_id} p={p} onClick={() => navigate(`/portfolio/${p.portfolio_id}`)} />
        ))}
      </div>
    </div>
  );
}

function PortfolioCard({ p, onClick }) {
  const isActive = p.status === "active";
  const needsValue = !p.initial_value;
  const pnlClass = p.pnl_pct == null ? "text-zinc-500" : p.pnl_pct > 0 ? "text-profit" : p.pnl_pct < 0 ? "text-loss" : "text-zinc-700";
  return (
    <Card data-testid={`portfolio-card-${p.portfolio_id}`} onClick={onClick} className="border-zinc-200 shadow-none cursor-pointer hover:border-zinc-400 transition-colors">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <Badge className="bg-purple-50 text-purple-700 border-purple-200">{p.strategy_name}</Badge>
            <div className="font-display font-bold text-xl text-zinc-950 mt-2">{p.strategy_name}</div>
            <div className="text-[11px] text-zinc-500 font-mono mt-1">{fmtDate(p.start_date)} → {fmtDate(p.end_date)}</div>
          </div>
          <ChevronRight size={16} className="text-zinc-400 mt-1" />
        </div>
        <div className="flex items-center gap-2">
          <Badge className={isActive ? "bg-green-50 text-green-700 border-green-200" : "bg-zinc-100 text-zinc-500 border-zinc-200"}>{isActive ? "Active" : "Inactive"}</Badge>
          {p.is_complimentary && <Badge className="bg-amber-50 text-amber-700 border-amber-200">Complimentary</Badge>}
        </div>
        {needsValue ? (
          <div className="pt-2 border-t border-zinc-100">
            <div className="text-xs text-amber-700 font-semibold">Action required</div>
            <div className="text-xs text-zinc-500 mt-1">Define initial value to activate this portfolio.</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-zinc-100">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Current</div>
              <div className="font-mono text-base text-zinc-950 mt-0.5">{inr(p.current_value, { decimals: 0 })}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">P&amp;L</div>
              <div className={`font-mono text-base mt-0.5 ${pnlClass}`}>{p.pnl_pct == null ? "—" : pct(p.pnl_pct)}</div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DefineValueForm({ portfolioId, onDone, refresh }) {
  const [val, setVal] = useState(500000);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (val < 500000) { toast.error("Minimum value is ₹5,00,000"); return; }
    setBusy(true);
    try {
      await api.post(`/customer/portfolios/${portfolioId}/define-value`, { initial_value: Number(val) });
      toast.success("Portfolio activated. You'll start receiving recommendations.");
      onDone();
      refresh && refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="border-zinc-200 shadow-none max-w-2xl">
      <CardContent className="p-10">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Step 1</div>
        <h2 className="font-display font-bold text-3xl text-zinc-950 mt-2">Define your portfolio value</h2>
        <p className="text-sm text-zinc-500 mt-2 mb-8">Set the corpus you want managed for this strategy. Minimum: <span className="font-mono text-zinc-900">₹ 5,00,000</span>. The full amount is held as cash until the engine starts deploying it.</p>
        <form onSubmit={submit} className="space-y-4 max-w-sm">
          <div>
            <Label>Portfolio amount (₹)</Label>
            <Input data-testid="define-value-input" type="number" min={500000} step={50000} value={val} onChange={(e) => setVal(Number(e.target.value))} className="mt-1 font-mono text-lg" />
          </div>
          <Button data-testid="define-value-submit" disabled={busy} type="submit" className="bg-zinc-950 hover:bg-zinc-800 text-white">
            {busy ? "Activating..." : "Activate portfolio"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
